import { useState, useEffect, useCallback } from 'react';

/**
 * Cookie helper: read a cookie value by name.
 */
function getCookieValue(name) {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Consent checker functions per plugin slug.
 * Each returns true if the user has given consent.
 *
 * Every checker tries the JS API first, then falls back to reading the
 * plugin's cookie directly.  This avoids race conditions where React
 * mounts before the plugin's global JS object is available.
 */
const consentCheckers = {
    wpconsent: function () {
        try {
            // JS API.
            if (window.WPConsent && window.WPConsent.hasConsent) {
                return !!window.WPConsent.hasConsent('marketing');
            }
            // Cookie fallback: wpconsent_preferences = {"marketing":true,...}
            const cookie = getCookieValue('wpconsent_preferences');
            if (cookie) {
                const prefs = JSON.parse(cookie);
                return prefs && prefs.marketing === true;
            }
        } catch (e) {
            // Not consented.
        }
        return false;
    },

    real_cookie_banner: function () {
        try {
            const consent = document.cookie.match(/real_cookie_banner[^=]*=([^;]+)/);
            if (consent && consent[1]) {
                const decoded = decodeURIComponent(consent[1]);
                // The cookie value ends with JSON: {"d":{...},"bc":"main_all"}
                // "bc" = button clicked: "main_all" (Accept All), "main_essential" (Essential Only), "ind" (Individual).
                // "d" = consented group/service IDs (numeric).
                const jsonStart = decoded.indexOf('{');
                if (jsonStart !== -1) {
                    const data = JSON.parse(decoded.substring(jsonStart));
                    // Accept All.
                    if (data.bc === 'main_all') {
                        return true;
                    }
                    // Individual selection: "d" contains consented non-essential services.
                    if (data.d && Object.keys(data.d).length > 0) {
                        return true;
                    }
                }
            }
        } catch (e) {
            // Not consented.
        }
        return false;
    },

    cookie_notice: function () {
        try {
            return getCookieValue('cookie_notice_accepted') === 'true';
        } catch (e) {
            return false;
        }
    },

    cookie_law_info: function () {
        try {
            // JS API (returns the string 'yes' or 'no', not a boolean).
            if (window.cookieyes && window.cookieyes._ckyConsentStore && window.cookieyes._ckyConsentStore.get) {
                const result = window.cookieyes._ckyConsentStore.get('functional');
                return result === true || result === 'yes';
            }
            // Cookie fallback: cookieyes-consent contains "functional:yes".
            const cookie = getCookieValue('cookieyes-consent');
            if (cookie) {
                return cookie.indexOf('functional:yes') !== -1;
            }
        } catch (e) {
            // Not consented.
        }
        return false;
    },

    cookiebot: function () {
        try {
            // JS API.
            if (window.Cookiebot && window.Cookiebot.consent) {
                return !!window.Cookiebot.consented && !!window.Cookiebot.consent.marketing;
            }
            // Cookie fallback: CookieConsent is a JS object literal (NOT JSON).
            // Value of -1 means consent is not required in the visitor's region.
            const cookie = getCookieValue('CookieConsent');
            if (cookie) {
                if (cookie === '-1') {
                    return true;
                }
                const match = cookie.match(/marketing:(true|false)/);
                return match ? match[1] === 'true' : false;
            }
        } catch (e) {
            // Not consented.
        }
        return false;
    },

    complianz: function () {
        try {
            // JS API.
            if (typeof window.cmplz_has_consent === 'function') {
                return !!window.cmplz_has_consent('marketing');
            }
            // Cookie fallback: cmplz_marketing = "allow" | "deny" | "".
            const cookie = getCookieValue('cmplz_marketing');
            if (cookie) {
                return cookie === 'allow';
            }
        } catch (e) {
            // Not consented.
        }
        return false;
    },

    borlabs: function () {
        try {
            if (window.BorlabsCookie && window.BorlabsCookie.Consents && window.BorlabsCookie.Consents.hasConsent) {
                return window.BorlabsCookie.Consents.hasConsent('tiktok') || window.BorlabsCookie.Consents.hasConsent('marketing');
            }
        } catch (e) {
            // Not consented.
        }
        return false;
    },

    moove: function () {
        try {
            const cookie = getCookieValue('moove_gdpr_popup');
            if (cookie) {
                const decoded = JSON.parse(decodeURIComponent(cookie));
                return decoded && (decoded.thirdparty === '1' || decoded.thirdparty === 1);
            }
        } catch (e) {
            // Not consented.
        }
        return false;
    }
};

/**
 * React hook for GDPR consent state.
 *
 * Reads GDPR config from `window.sbtt_gdpr_options` (set by PHP),
 * checks consent on mount, and listens for consent changes from
 * all supported GDPR plugins.
 *
 * @returns {{ gdprActive: boolean, consented: boolean, placeholder: string, avatarPlaceholder: string }}
 */
const useGdprConsent = () => {
    const options = window.sbtt_gdpr_options || {};
    const gdprActive = !!options.gdprActive;
    const activePlugin = options.activePlugin || '';
    const placeholder = options.placeholder || '';
    const avatarPlaceholder = options.avatarPlaceholder || '';

    const checkConsent = useCallback(() => {
        if (!gdprActive || !activePlugin || !consentCheckers[activePlugin]) {
            return false;
        }
        return consentCheckers[activePlugin]();
    }, [gdprActive, activePlugin]);

    // If GDPR is not active, consent is always true (render normally).
    const [consented, setConsented] = useState(!gdprActive || checkConsent());

    useEffect(() => {
        if (!gdprActive || consented) {
            return;
        }

        // Re-check on mount in case consent was given between PHP render and React mount.
        if (checkConsent()) {
            setConsented(true);
            return;
        }

        const onConsent = () => {
            if (checkConsent()) {
                setConsented(true);
            }
        };

        // Event-based listeners for plugins that fire custom events.
        // WPConsent: wpconsent_consent_saved (window), wpconsent_consent_processed (document).
        window.addEventListener('wpconsent_consent_saved', onConsent);
        document.addEventListener('wpconsent_consent_processed', onConsent);
        // Cookiebot: CookiebotOnAccept (window).
        window.addEventListener('CookiebotOnAccept', onConsent);
        // Complianz: cmplz_status_change, cmplz_fire_categories (document).
        document.addEventListener('cmplz_status_change', onConsent);
        document.addEventListener('cmplz_fire_categories', onConsent);
        // Borlabs Cookie v3: borlabs-cookie-consent-saved (window).
        window.addEventListener('borlabs-cookie-consent-saved', onConsent);
        // Real Cookie Banner: RCBConsentChange (window).
        window.addEventListener('RCBConsentChange', onConsent);
        // Cookie Notice (dFactory): setCookieNotice (document).
        document.addEventListener('setCookieNotice', onConsent);
        // CookieYes: cookieyes_consent_update (document).
        document.addEventListener('cookieyes_consent_update', onConsent);

        // Click handler for common consent button patterns (fallback).
        const onConsentClick = (e) => {
            const target = e.target;
            if (!target) return;

            const isConsentButton = (
                target.classList.contains('cky-btn-accept') ||             // CookieYes
                target.classList.contains('cn-set-cookie') ||              // Cookie Notice (dFactory)
                target.classList.contains('moove-gdpr-infobar-allow-all') || // Moove
                target.id === 'cn-accept-cookie' ||                        // Cookie Notice (dFactory)
                target.id === 'CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll' // Cookiebot
            );

            if (isConsentButton) {
                setTimeout(onConsent, 500);
            }
        };
        document.addEventListener('click', onConsentClick, true);

        // Real Cookie Banner Pro: consentApi.consent() returns a Promise that
        // resolves when consent is given for a specific service unique name.
        // This handles the case where a site owner configured a "tiktok" service.
        if (activePlugin === 'real_cookie_banner' && window.consentApi && window.consentApi.consent) {
            window.consentApi.consent('tiktok')
                .then(function (result) { if (result && result.consentGiven) setConsented(true); })
                .catch(function () {});
        }

        // Polling fallback for plugins that only set cookies (no custom events).
        let pollId = null;
        let pollTimeoutId = null;
        if (activePlugin === 'moove') {
            pollId = setInterval(() => {
                if (checkConsent()) {
                    clearInterval(pollId);
                    if (pollTimeoutId) clearTimeout(pollTimeoutId);
                    setConsented(true);
                }
            }, 1000);
            // Stop polling after 5 minutes.
            pollTimeoutId = setTimeout(() => {
                if (pollId) clearInterval(pollId);
            }, 300000);
        }

        return () => {
            window.removeEventListener('wpconsent_consent_saved', onConsent);
            document.removeEventListener('wpconsent_consent_processed', onConsent);
            window.removeEventListener('CookiebotOnAccept', onConsent);
            document.removeEventListener('cmplz_status_change', onConsent);
            document.removeEventListener('cmplz_fire_categories', onConsent);
            window.removeEventListener('borlabs-cookie-consent-saved', onConsent);
            window.removeEventListener('RCBConsentChange', onConsent);
            document.removeEventListener('setCookieNotice', onConsent);
            document.removeEventListener('cookieyes_consent_update', onConsent);
            document.removeEventListener('click', onConsentClick, true);
            if (pollId) clearInterval(pollId);
            if (pollTimeoutId) clearTimeout(pollTimeoutId);
        };
    }, [gdprActive, consented, checkConsent]);

    return { gdprActive, consented, placeholder, avatarPlaceholder };
};

export default useGdprConsent;
